import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { useRouterState } from '@tanstack/react-router'
import LiveClock from './LiveClock'
import Magnetic from './Magnetic'
import Marquee from './Marquee'
import Reveal from './Reveal'
import SquiggleAccent from '@/components/editorial/SquiggleAccent'
import { portfolioData } from '@/data/portfolio'
import { pageMarkdownHref } from '@/lib/pageDiscovery'

export default function PreviewFooter() {
  const { personal } = portfolioData
  const currentYear = new Date().getFullYear()
  const markdownHref = useRouterState({
    select: (state) =>
      pageMarkdownHref(state.matches[state.matches.length - 1]),
  })

  return (
    <footer
      id="p-contact"
      className="relative scroll-mt-16 overflow-hidden bg-ink pt-20 text-cream-50 sm:pt-28"
    >
      <div className="container-custom">
        <Reveal>
          <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.24em] text-sage-200">
            Contact
          </p>
          <h2 className="mt-6 max-w-5xl font-heading text-[clamp(2.8rem,8vw,6.5rem)] leading-[0.92] tracking-[-0.045em]">
            Have a project or an{' '}
            <SquiggleAccent className="italic text-sage-200">
              opportunity in mind?
            </SquiggleAccent>
          </h2>
          <div className="mt-12 grid gap-10 border-t border-white/15 pt-10 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_0.8fr] lg:gap-16">
            <div className="flex flex-col items-start gap-6">
              <p className="text-sm leading-7 text-cream-50/80 sm:text-base sm:leading-8">
                Tell me about the website or application you want to build, or
                the developer role you’re hiring for. Based in Erode, Tamil
                Nadu, and open to working with businesses and teams in India and
                worldwide.
              </p>
              <Magnetic>
                <a
                  href={`mailto:${personal.email}?subject=Project%20enquiry`}
                  className="focus-ring inline-flex items-center gap-2 rounded-full bg-sage-200 px-7 py-4 text-sm font-bold text-forest-strong transition-colors hover:bg-cream-50"
                >
                  Discuss a project
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Magnetic>
              <p className="preview-mono text-[10px] uppercase leading-5 tracking-[0.18em] text-cream-50/65">
                Erode, IN — <LiveClock /> IST
              </p>
            </div>
            <div className="flex flex-col items-start gap-4 text-sm">
              <p className="preview-mono mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50/50">
                Email
              </p>
              <a
                href={`mailto:${personal.email}?subject=Developer%20role%20enquiry`}
                className="focus-ring font-semibold text-sage-200 underline underline-offset-4"
              >
                Contact about a role
              </a>
              <a
                href={`mailto:${personal.email}`}
                className="focus-ring break-all text-cream-50/75"
              >
                {personal.email}
              </a>
            </div>
            <div className="flex flex-col items-start gap-4">
              <p className="preview-mono mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50/50">
                Elsewhere
              </p>
              {personal.socials.map((social) => (
                <a
                  key={social.platform}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="preview-link-sweep preview-mono focus-ring inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-cream-50/70 transition-colors hover:text-sage-200"
                >
                  {social.platform}
                  <ArrowUpRight className="h-3 w-3" />
                </a>
              ))}
              <a
                href={personal.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="preview-link-sweep preview-mono focus-ring inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-sage-200"
              >
                Resume
                <ArrowUpRight className="h-3 w-3" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-16 sm:mt-24">
        <Marquee
          duration={40}
          className="preview-name-marquee py-6"
          itemClassName="preview-outline-text"
          items={[
            'Suriyaprakash Somu',
            'Full-Stack Developer',
            'Erode → Worldwide',
          ]}
        />
      </div>

      <div className="container-custom flex flex-col gap-3 border-t border-white/10 py-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="preview-mono text-[10px] uppercase tracking-[0.2em] text-cream-50/65">
          © {currentYear} — Designed & built in Erode
        </p>
        <div className="flex items-center gap-6">
          {markdownHref && (
            <a
              href={markdownHref}
              className="preview-mono focus-ring text-[10px] font-bold uppercase tracking-[0.2em] text-sage-200 underline underline-offset-4"
            >
              View Markdown
            </a>
          )}
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="preview-mono focus-ring inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50/65 transition-colors hover:text-sage-200"
          >
            Back to top
            <ArrowUp className="h-3 w-3" />
          </button>
        </div>
      </div>
    </footer>
  )
}
